import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// Attach an email to a lead — pick an existing open lead, or create a NEW lead from the email
// (AI pre-fills name / company / email / phone / intro from the email for you to review).
// Used by the Email module's "Add to lead" action.

export interface EmailRef { id: string; subject: string; fromName: string; fromEmail: string; date: any; bodyText?: string }

function toUnixSeconds(d: any): number {
  if (!d) return Math.floor(Date.now() / 1000);
  if (d instanceof Date) return Math.floor(d.getTime() / 1000);
  if (typeof d === 'number') return Math.floor(d > 1e12 ? d / 1000 : d);
  const ms = new Date(d).getTime(); return Math.floor((isNaN(ms) ? Date.now() : ms) / 1000);
}

const ddl: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', borderRadius: 9, outline: 'none', boxSizing: 'border-box', color: 'var(--text-primary)' };
const lbl: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const G = '#6D28D9';

export default function AddToLeadModal({ email, emails, current, onClose, onDone }: { email?: EmailRef; emails?: EmailRef[]; current?: { rowId?: string; id: string; name?: string; company?: string }; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  // Accept one email (existing callers) or many (bulk "add to lead" from the list).
  const list: EmailRef[] = (emails && emails.length) ? emails : (email ? [email] : []);
  const primary: EmailRef = list[0] || { id: '', subject: '', fromName: '', fromEmail: '', date: 0 };
  const [mode, setMode] = useState<'pick' | 'create'>('pick');
  const [leads, setLeads] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [f, setF] = useState<any>({ companyId: '', name: '', company: '', email: '', phone: '', intro: '' });
  const [dupes, setDupes] = useState<any[]>([]);           // existing leads that look like the same customer

  useEffect(() => {
    // Every status, not just open: a returning customer's old lead is often already won/lost, and
    // hiding it is what pushes staff to click "create new" and mint a duplicate.
    api.leadList({ scope: 'all' }).then((d: any) => setLeads(d.rows || [])).catch((e: any) => setErr(e.message));
    api.leadCompanies().then((d: any) => setCompanies(d.rows || [])).catch(() => {});
  }, []);

  const payloadOf = (e: EmailRef) => ({ emailId: e.id, subject: e.subject, fromName: e.fromName, fromEmail: e.fromEmail, date: toUnixSeconds(e.date) });

  // Attach to `leadId`. When the email already sits in ANOTHER lead this is a MOVE, so the old link
  // is removed afterwards — otherwise "change lead" would silently leave the email in both.
  const attach = async (leadId: string) => {
    setBusy(true); setErr('');
    let done = 0;
    try {
      for (const e of list) { await api.leadAttachEmail(leadId, payloadOf(e)); done++; }
      if (current?.rowId && current.id !== leadId) await api.leadDetachEmail(current.rowId, 'thread');
      onDone(); onClose();
    } catch (e: any) {
      setErr(e.message); setBusy(false);
      if (done > 0) onDone();   // some already landed — let the caller refresh so the UI isn't stale
    }
  };

  // Enter create mode → AI pre-fills the form from the (first) email.
  const startCreate = async () => {
    setMode('create'); setExtracting(true); setErr('');
    try {
      const r: any = await api.leadFromEmail({ subject: primary.subject, fromName: primary.fromName, fromEmail: primary.fromEmail, bodyText: primary.bodyText || '' });
      setF((p: any) => ({ ...p, companyId: companies[0]?.id || '', name: r.name || primary.fromName || primary.fromEmail, company: r.company || '', email: r.email || primary.fromEmail, phone: r.phone || '', intro: r.intro || primary.subject }));
    } catch {
      setF((p: any) => ({ ...p, companyId: companies[0]?.id || '', name: primary.fromName || primary.fromEmail, email: primary.fromEmail, intro: primary.subject }));
    } finally { setExtracting(false); }
  };

  // Warn about existing leads for the same customer BEFORE a new one is created.
  useEffect(() => {
    if (mode !== 'create' || extracting) return;
    const h = setTimeout(() => {
      api.leadDupeCheck({ email: f.email, name: f.name, company: f.company })
        .then((d: any) => setDupes(d.rows || [])).catch(() => setDupes([]));
    }, 350);
    return () => clearTimeout(h);
  }, [mode, extracting, f.email, f.name, f.company]);

  const createAndAttach = async () => {
    if (!String(f.name || '').trim() && !String(f.company || '').trim()) { setErr(t('ld_need_name')); return; }
    setBusy(true); setErr('');
    try {
      const r: any = await api.leadCreate({ ...f, source: 'email', status: 'open' });
      for (const e of list) await api.leadAttachEmail(r.id, payloadOf(e));
      onDone(); onClose();
    } catch (e: any) { setErr(e.message); setBusy(false); }
  };

  // Remove this email from its lead. Thread-scoped by default: leaving other messages of the same
  // conversation behind means the customer's next reply auto-files straight back into this lead.
  const removeCurrent = async () => {
    if (!current?.rowId) return;
    if (!window.confirm(t('ld_detach_confirm', { subject: primary.subject || '(no subject)' }))) return;
    let siblings = 0;
    try { siblings = ((await api.leadThreadCount(current.rowId)) as any).siblings || 0; } catch { /* best effort */ }
    const scope = siblings === 0 || window.confirm(t('ld_detach_thread', { n: siblings })) ? 'thread' : undefined;
    setBusy(true); setErr('');
    try { await api.leadDetachEmail(current.rowId, scope as any); onDone(); onClose(); }
    catch (e: any) { setErr(e.message); setBusy(false); }
  };
  const set = (k: string) => (e: any) => setF((p: any) => ({ ...p, [k]: e.target.value }));
  const filtered = leads.filter(l => !q || (l.name || '').toLowerCase().includes(q.toLowerCase()) || (l.company || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => e.stopPropagation()} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 480, maxWidth: '94vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, padding: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>{mode === 'create' ? t('ld_create_from_email') : t('ld_add_to')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>✉️ {list.length > 1 ? `${list.length} emails` : (primary.subject || '(no subject)')} · {primary.fromName || primary.fromEmail}</div>

        {current && mode === 'pick' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', borderRadius: 9, background: '#F5F3FF', border: `1px solid ${G}`, marginBottom: 12 }}>
            <i className="ti ti-target-arrow" style={{ color: G, fontSize: 14 }} />
            <div style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{t('ld_current')}</div>
              <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current.name || current.company || '—'}</div>
            </div>
            {current.rowId && (
              <button onClick={removeCurrent} disabled={busy} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #DC2626', background: 'transparent', color: '#DC2626', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <i className="ti ti-x" /> {t('ld_remove_from')}
              </button>
            )}
          </div>
        )}

        {mode === 'pick' ? (
          <>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('ld_search')} style={{ ...ddl, marginBottom: 10 }} />
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 9, marginBottom: 12, minHeight: 120, maxHeight: 280 }}>
              {filtered.length === 0 ? <div style={{ padding: 18, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12.5 }}>{t('ld_none')}</div>
                : filtered.map(l => (
                  <div key={l.id} onClick={() => !busy && attach(l.id)} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 8 }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <span style={{ fontSize: 13, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name || l.company || '—'}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
                      <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{l.owner_name || ''}</span>
                      {l.status !== 'open' && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: l.status === 'won' ? '#ECFDF5' : '#FEF2F2', color: l.status === 'won' ? '#15803D' : '#DC2626' }}>{t('ld_' + l.status)}</span>}
                    </span>
                  </div>
                ))}
            </div>
            {err && <div style={{ fontSize: 12, color: '#E24B4A', marginBottom: 8 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={startCreate} disabled={busy} style={{ flex: 1, padding: '10px', borderRadius: 9, border: `1px dashed ${G}`, background: 'transparent', color: G, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>＋ {t('ld_create_from_email')}</button>
              <button onClick={onClose} style={{ padding: '10px 18px', borderRadius: 9, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
              {extracting && <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}><i className="ti ti-loader-2" style={{ fontSize: 24, color: G, animation: 'spin 0.8s linear infinite' }} /><div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('ld_extracting')}</div></div>}
              {dupes.length > 0 && (
                <div style={{ border: '1px solid #FDE68A', background: '#FFFBEB', borderRadius: 9, padding: '10px 12px', marginBottom: 11 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', marginBottom: 6 }}><i className="ti ti-alert-triangle" /> {t('ld_dupe_warn')}</div>
                  {dupes.map(d => (
                    <div key={d.id} onClick={() => !busy && attach(d.id)} title={t('ld_dupe_use')} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 7, cursor: 'pointer', background: 'var(--bg-surface)', marginBottom: 4 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || d.company || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{[d.email, d.owner_name].filter(Boolean).join(' · ')}</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: G, whiteSpace: 'nowrap' }}>{t('ld_dupe_use')} →</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}><label style={lbl}>{t('ld_name')} *</label><input value={f.name} onChange={set('name')} style={ddl} /></div>
                  <div style={{ flex: 1 }}><label style={lbl}>{t('ld_lead_company')}</label><input value={f.company} onChange={set('company')} style={ddl} /></div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}><label style={lbl}>Email</label><input value={f.email} onChange={set('email')} style={ddl} /></div>
                  <div style={{ flex: 1 }}><label style={lbl}>{t('ld_phone')}</label><input value={f.phone} onChange={set('phone')} style={ddl} /></div>
                </div>
                <div><label style={lbl}>{t('ld_firm')}</label><select value={f.companyId} onChange={set('companyId')} style={ddl}>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                <div><label style={lbl}>{t('ld_intro')}</label><textarea value={f.intro} onChange={set('intro')} rows={3} style={{ ...ddl, resize: 'vertical' }} /></div>
              </div>
            </div>
            {err && <div style={{ fontSize: 12, color: '#E24B4A', margin: '10px 0 0' }}>{err}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button onClick={() => setMode('pick')} style={{ padding: '10px 16px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('ld_back')}</button>
              <div style={{ flex: 1 }} />
              <button onClick={createAndAttach} disabled={busy || extracting} style={{ padding: '10px 22px', borderRadius: 9, border: 'none', background: G, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: busy || extracting ? 0.6 : 1 }}>{t('ld_create_attach')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
